<template>
  <div v-if="initialised" class="cell-container">
    <appointment-cell
      v-for="appointment in appointments"
      :key="appointment.id"
      :appointment="appointment"
    />

    <unavailability-cell
      v-if="publicHoliday"
      :adjust="false"
      :length="13"
      :unavailability="publicHoliday"
      type="public-holiday"
    />

    <unavailability-cell
      v-if="unavailable"
      :adjust="unavailabilityAdjust"
      :length="unavailabilityLength"
      :unavailability="unavailable"
      type="unavailable"
    />

    <suspended-row v-if="data.status === 'Suspended'" />
  </div>
</template>

<script>
import moment from "moment";
import EventBus from "../../../imports/event-bus";
import calendar from "../../../mixins/calendar";
import AppointmentCell from "./AppointmentCell.vue";
import UnavailabilityCell from "./UnavailabilityCell.vue";
import SuspendedRow from "./SuspendedRow.vue";

export default {
  components: {
    AppointmentCell,
    UnavailabilityCell,
    SuspendedRow,
  },

  mixins: [calendar],
  props: ["data", "column"],

  data() {
    return {
      tableDate: null,
      cellHour: null,
      cellMoment: null,

      appointments: [],
      initialised: false,

      unavailable: false,
      unavailabilityLength: 1,
      unavailabilityAdjust: false,

      publicHoliday: false,
    };
  },

  watch: {
    data() {
      this.init();
    },

    column() {
      this.init();
    },
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      this.initialised = false;
      this.tableDate = this.$parent.$parent.telescopeMode
        ? this.data.date
        : this.$parent.$parent.date;
      this.cellHour = this.calendar.hourMap[this.column];

      // Parse public holiday
      if (this.column === "0_am" || this.column === "6_am" || this.column === "12_pm") {
        if (this.data.addresses && this.data.addresses[0]) {
          const publicHoliday = this.$store.getters["public-holidays/getPublicHolidayByAddress"](
            this.data.addresses[0]
          );

          if (publicHoliday) {
            this.publicHoliday = {
              reason: publicHoliday.name,
              inStates: publicHoliday.inStates,
            };
          } else {
            this.publicHoliday = false;
          }
        }
      }

      this.$nextTick(() => {
        this.appointments = this.validAppointmentsAtCurrentHour(this.data.appointments);
        this.unavailable = this.validUnavailabilityAtCurrentHour();

        this.initialised = true;
      });
    },

    // Initialise unavailability
    // Loop through provided unavailabilities, and return valid unavailability if:
    // 1: Haven't been drawn yet, and
    // 2: Current cell hour is in between defined unavailability
    // Note: Current logic doesn't handle two unavailabilities start both within the same hour
    validUnavailabilityAtCurrentHour() {
      let validRecord = false;
      const unavailabilities = this.$lodash.get(this.data, "unavailabilities", []);

      // Loop thru all unavailabilities
      unavailabilities.forEach((unavailability) => {
        // Condition 1: Haven't been drawn yet
        if (
          !this.$store.getters["tech-availability/unavailabilityAlreadyDrawn"](unavailability.id)
        ) {
          // Calculate start/end/current
          const start = moment(unavailability.start_from);
          const end = moment(unavailability.end_by);
          const hour = this.cellHour < 10 ? `0${this.cellHour}` : this.cellHour;
          this.cellMoment = moment(`${this.tableDate} ${hour}:00:00`);

          // Condition 2: Current cell hour is in between defined unavailability
          if (
            this.cellMoment.isBetween(start.clone().subtract(1, "hour"), end.clone().add(1, "hour"))
          ) {
            // The unavailability is a validRecord!
            validRecord = unavailability;

            // Now, calculate cell position and length
            this.unavailabilityLength = end.diff(this.cellMoment, "minutes") / 60;
            if (start.clone().subtract(30, "minute").isSame(this.cellMoment)) {
              // Half width cell, right half
              this.unavailabilityAdjust = true;
              this.unavailabilityLength = this.unavailabilityLength - 0.5;
            } else {
              // Full width cell
              this.unavailabilityAdjust = false;
            }

            // Drawing, add to drawnUnavailabilityIds
            this.$store.commit("tech-availability/addDrawnUnavailabilityId", validRecord.id);
          }
        }
      });

      return validRecord;
    },

    // Pick valid appointments that occur within current hour cell
    validAppointmentsAtCurrentHour(appointments) {
      return appointments.filter((appointment) => {
        const time = moment(appointment.time);
        const start = time.hour() + time.minute() / 60;
        const end = start + parseFloat(appointment.duration);

        // If invalid, skip
        if (
          !appointment.id ||
          !appointment.time ||
          !this.$lodash.has(appointment, "job.status") ||
          !this.$lodash.has(appointment, "job.job_contacts[0].id")
        ) {
          return false;
        }

        // If already drawn, skip
        if (this.$store.getters["tech-availability/appointmentAlreadyDrawn"](appointment.id)) {
          return false;
        }

        // Check before/after hours appointments
        if (start < 6 && start > 0) {
          EventBus.$emit("contain-before-hours");
        }
        if (start >= 19 && start !== 0 && start < 23.5) {
          EventBus.$emit("contain-after-hours");
        }

        // If not happening in current cellHour, skip
        if (this.cellHour < Math.floor(start) || this.cellHour >= end) {
          return false;
        }

        // Drawing, add to drawnAppointmentIds
        this.$store.commit("tech-availability/addDrawnAppointmentId", appointment.id);

        return true;
      });
    },
  },
};
</script>
